import type { MultiSelectOption } from '@snapchat/snap-design-system-marketing';
import { MultiSelect } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';

import { jobMultiSelectArrowCss, jobMultiSelectCss, jobMultiSelectWrapperCss } from './styles';

export interface JobMultiSelectFiltersProps {
  /**
   * Options shown in the dropdown; `id` values are what the parent stores and receives via
   * `onChange`.
   */
  items: MultiSelectOption[];
  /** Placeholder / summary label for the control (e.g. “All locations”). */
  title: string;
  /** Option IDs that are currently selected. */
  selectedValues: string[];
  /** Called with the full set of selected option IDs whenever the user adds or removes a selection. */
  onChange: (selectedIds: string[]) => void;
}

/**
 * Multi-select control for a single jobs filter (location, role, type) on the careers page.
 *
 * Wraps the marketing design system {@link MultiSelect} with job-search styling and keeps the parent
 * in terms of string IDs only.
 */
export const JobMultiSelectFilters: FC<JobMultiSelectFiltersProps> = ({
  items,
  title,
  selectedValues,
  onChange,
}) => {
  const selected = useMemo(
    () => items.filter(item => selectedValues.includes(item.id)),
    [items, selectedValues]
  );

  const handleSelect = useCallback(
    (selectedList: MultiSelectOption[]) => {
      onChange(selectedList.map(item => item.id));
    },
    [onChange]
  );

  const handleRemove = useCallback(
    (selectedList: MultiSelectOption[]) => {
      onChange(selectedList.map(item => item.id));
    },
    [onChange]
  );

  return (
    <MultiSelect
      options={items}
      selectedValues={selected}
      onSelect={handleSelect}
      onRemove={handleRemove}
      displayValue="name"
      placeholder={title}
      showCheckbox={true}
      dropdownClassName={jobMultiSelectCss}
      wrapperClassName={jobMultiSelectWrapperCss}
      arrowClassName={jobMultiSelectArrowCss}
      maxVisibleChips={2}
    />
  );
};

JobMultiSelectFilters.displayName = 'JobMultiSelectFilters';
